import { Form } from 'antd';
import type { FormInstance } from 'antd';
import { Dialog } from '@base-ui/react/dialog';
import { FloppyDiskIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@admin-panel/ui';
import LookContentEditor, {
  type LookContentFormValues,
} from '../../../../../components/LookContentEditor';

import {
  EditableSection,
  getEditorSectionFor,
  getEditingSectionTitle,
  WorkflowRecommendation,
} from '../helpers';

interface EditSectionDialogProps {
  editingSection: EditableSection;
  editForm: FormInstance<LookContentFormValues>;
  recommendationDefaults: Array<WorkflowRecommendation>;
  portalContainer?: HTMLElement | null;
  isUpdating: boolean;
  onCancel: () => void;
  onReset: () => void;
  onSave: () => void;
}

export const EditSectionDialog = ({
  editingSection,
  editForm,
  recommendationDefaults,
  portalContainer,
  isUpdating,
  onCancel,
  onReset,
  onSave,
}: EditSectionDialogProps) => {
  return (
    <Dialog.Root
      open={Boolean(editingSection)}
      onOpenChange={open => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <Dialog.Portal container={portalContainer ?? undefined}>
        <Dialog.Backdrop className="fixed inset-0 z-[1400] bg-black/35 backdrop-blur-sm" />
        <Dialog.Popup className="fixed inset-0 z-[1401] grid place-items-center p-4 focus-visible:outline-none">
          <div className="flex max-h-[85vh] w-[min(96vw,960px)] flex-col overflow-hidden rounded-xl bg-background shadow-2xl ring-1 ring-border/70">
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <Dialog.Title className="m-0 text-base font-semibold text-foreground">
                {editingSection ? `Edit ${getEditingSectionTitle(editingSection)}` : ''}
              </Dialog.Title>
              <Button onClick={onCancel} size="small" variant="outline" aria-label="Close">
                <XIcon size={16} />
                Close
              </Button>
            </header>

            <div className="max-h-[70vh] overflow-y-auto p-3">
              {editingSection ? (
                <Form form={editForm} layout="vertical">
                  <LookContentEditor
                    form={editForm}
                    mode="full"
                    presentation="flat"
                    showMetadata
                    section={getEditorSectionFor(editingSection)}
                    initialProductRecommendations={recommendationDefaults}
                    productPickerPortalContainer={portalContainer}
                  />
                </Form>
              ) : null}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
              <Button
                variant="outline"
                size="small"
                onClick={onCancel}
                icon={<XIcon size={14} />}
                type="button"
              >
                Cancel
              </Button>
              <Button variant="outline" size="small" onClick={onReset} type="button">
                Reset
              </Button>
              <Button
                size="small"
                onClick={onSave}
                isDisabled={isUpdating}
                isLoading={isUpdating}
                icon={<FloppyDiskIcon size={14} />}
                type="button"
              >
                Save
              </Button>
            </footer>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
